/**
 * Dock.ts — fixed (pile-supported) rectangular dock with bollards.
 *
 * MVP scope (etap 6B.1):
 *   • Rigid, never moves. Deck top sits at `deckY = waterLevel + freeboard`.
 *   • Wooden bollards arranged along the dock edges. Each bollard is a
 *     `MooringAnchor` — boats can tie a `MooringLine` to it.
 *   • Pile-supported visualisation only (the pile geometry is generated for
 *     rendering but doesn't participate in any dynamics).
 *
 * Frame: dock-local axes are X = along length, Z = across width. World
 * position = (`posX`, `posZ`) at deck centre, rotated by `headingY` around
 * world Y. Bollards are stored in dock-local frame and projected to world
 * on demand via `getBollardWorldPos`.
 */

export interface DockBollardSpec {
  /** Stable identifier (shown in the UI dropdowns). */
  id: string;
  /** Dock-local X (along length). */
  x: number;
  /** Dock-local Z (across width). Use ±halfWidth + small inset. */
  z: number;
  /** Top height above the deck surface (m). Default 0.4. */
  topAboveDeck?: number;
}

/**
 * How boats moor along this dock's long sides. Each style implies a
 * specific bow-line endpoint:
 *  - "dock-only"  — bow + stern lines all to dock bollards (legacy).
 *  - "buoy"       — bow line goes to a free-floating buoy off the dock,
 *                   stern lines to the dock.
 *  - "mooring"    — bow line goes to a fixed mooring post in the water
 *                   (a permanent buoy on a chain), stern lines to dock.
 *  - "y-boom"     — finger pier; bow + stern cleated to a perpendicular
 *                   arm extending off the main dock.
 *  - "anchor"     — boat drops its own anchor off the bow, stern lines
 *                   to the dock.
 */
export type MooringStyle =
  | "dock-only"
  | "buoy"
  | "mooring"
  | "y-boom"
  | "anchor";

export interface DockSpec {
  id: string;
  /** Length along local +X (m). */
  length: number;
  /** Width along local +Z (m). */
  width: number;
  /** Deck plank thickness (m). */
  deckThickness: number;
  /** Distance from waterline to deck top (m). */
  freeboard: number;
  /** Number of piles per long side. Total = 2 × pilesPerSide. */
  pilesPerSide: number;
  /** Pile radius (m). */
  pileRadius: number;
  /** How far each pile extends BELOW the waterline (m). */
  pileDepth: number;
  /** How far each pile sticks ABOVE the deck (m). 0 = flush. */
  pileTopAboveDeck: number;
  /** Deck plank colour (RGB 0..1). */
  deckColor: [number, number, number];
  /** Pile colour. */
  pileColor: [number, number, number];
  /** Bollard colour. */
  bollardColor: [number, number, number];
  /** Bollard radius (m). */
  bollardRadius: number;
  /** Bollard total height above deck (m). */
  bollardHeight: number;
  /** Explicit bollard list (dock-local). */
  bollards: DockBollardSpec[];
  /**
   * How boats moor along this dock. Defaults to "dock-only".
   */
  mooringStyle?: MooringStyle;
  /**
   * Optional ramp / gangway connecting the dock to shore. Starts at one
   * short end of the deck and extends along world +X (dock-local) for
   * `length` metres. Width is across the dock-local Z axis.
   *
   * When the painted shore is sloped, the gangway can be rendered as a
   * tilted ramp or as a flight of stairs instead of a flat plank — see
   * `kind` and `shoreRise`. Alternatively, supply a pre-computed
   * `steps` profile (one box per step) to follow real terrain.
   */
  gangway?: {
    /** Which short end of the dock the gangway attaches to (-1 = -X end, +1 = +X end). */
    side: -1 | 1;
    /** Length of the gangway (m). Used as the horizontal run for ramps/stairs. */
    length: number;
    /** Width across the gangway (m). */
    width: number;
    /** Lateral offset from the deck centre (m, in dock-local Z). */
    offsetZ?: number;
    /**
     * Vertical delta from deck top to shore ground at the outer end (m).
     * Positive = shore is HIGHER than the deck (gangway climbs up).
     * Negative = shore is below the deck (gangway slopes down).
     * Defaults to 0 (flat plank).
     */
    shoreRise?: number;
    /**
     * Geometry style:
     *  - "plank"  — flat horizontal plank (default for shoreRise≈0)
     *  - "ramp"   — tilted plank from deck to shore ground
     *  - "stairs" — staircase of stacked boxes, treads run-tread sized
     */
    kind?: "plank" | "ramp" | "stairs";
    /**
     * Optional custom step profile. When present, overrides
     * `length`/`shoreRise`/`kind` — one stacked box is rendered per
     * entry. Each entry: `run` = horizontal length of this step (m),
     * `top` = top-surface height above the dock deck (m, can be 0 or
     * negative). Steps are placed sequentially starting at the dock
     * edge, marching outward in the +`side` direction.
     */
    steps?: Array<{ run: number; top: number }>;
  };
}

/** Default 12 m × 1.5 m wooden pier with 6 bollards (3 per long side). */
export function defaultDockSpec(id = "wooden_pier_12m"): DockSpec {
  const length = 12;
  const width = 1.8;
  const halfL = length * 0.5;
  const halfW = width * 0.5;
  // Bollard inset from the deck edge (operators stand inboard of them).
  const inset = 0.18;
  // 2 bollards per long side at L/4 and 3L/4 (no centre bollard).
  const lateralXs = [-halfL * 0.66, +halfL * 0.66];
  const bollards: DockBollardSpec[] = [];
  let pn = 1;
  for (const x of lateralXs) {
    bollards.push({ id: `P${pn}`, x, z: -halfW + inset });
    pn++;
  }
  let sn = 1;
  for (const x of lateralXs) {
    bollards.push({ id: `S${sn}`, x, z: +halfW - inset });
    sn++;
  }
  return {
    id,
    length,
    width,
    deckThickness: 0.18,
    freeboard: 0.7,
    pilesPerSide: 4,
    pileRadius: 0.11,
    pileDepth: 2.5,
    pileTopAboveDeck: 0.0,
    deckColor: [0.55, 0.42, 0.27],
    pileColor: [0.36, 0.26, 0.18],
    bollardColor: [0.22, 0.22, 0.22],
    bollardRadius: 0.055,
    bollardHeight: 0.25,
    bollards,
    gangway: {
      side: -1, // −X end of the deck
      length: 6,
      width: 1.2,
      offsetZ: 0,
    },
  };
}

/**
 * A `MooringAnchor` is anything a `MooringLine` can attach to. Implemented
 * by `Buoy` (movable) and by `DockBollard` (rigid world-space).
 */
export interface MooringAnchor {
  /** Stable id used by UI/state to refer to this anchor. */
  readonly anchorId: string;
  /** World-space position the rope visually & physically attaches to. */
  getWorldPos(): [number, number, number];
  /** Apply line tension (rigid anchors ignore). */
  applyExternalForce(fx: number, fy: number, fz: number): void;
}

/**
 * A single bollard on a dock. Cached world position is recomputed if the
 * dock ever moves (floating dock variant — for now stationary docks just
 * compute once).
 */
export class DockBollard implements MooringAnchor {
  readonly anchorId: string;
  private worldX: number;
  private worldY: number;
  private worldZ: number;

  constructor(id: string, x: number, y: number, z: number) {
    this.anchorId = id;
    this.worldX = x;
    this.worldY = y;
    this.worldZ = z;
  }
  setWorldPos(x: number, y: number, z: number): void {
    this.worldX = x;
    this.worldY = y;
    this.worldZ = z;
  }
  getWorldPos(): [number, number, number] {
    return [this.worldX, this.worldY, this.worldZ];
  }
  /** Rigid bollard absorbs any line tension into the (idealised) structure. */
  applyExternalForce(_fx: number, _fy: number, _fz: number): void {
    /* no-op */
  }
}

export class Dock {
  readonly spec: DockSpec;
  /** World position of deck centre. */
  readonly posX: number;
  readonly posZ: number;
  /** Heading (Y rotation, radians). 0 = local +X aligns with world +X. */
  readonly headingY: number;
  /** World Y of the deck top surface. */
  readonly deckTopY: number;
  readonly bollards: DockBollard[] = [];

  constructor(
    spec: DockSpec,
    opts: {
      posX: number;
      posZ: number;
      headingY: number;
      waterLevel: number;
    },
  ) {
    this.spec = spec;
    this.posX = opts.posX;
    this.posZ = opts.posZ;
    this.headingY = opts.headingY;
    this.deckTopY = opts.waterLevel + spec.freeboard;
    const cosY = Math.cos(opts.headingY);
    const sinY = Math.sin(opts.headingY);
    for (const b of spec.bollards) {
      const wx = opts.posX + b.x * cosY - b.z * sinY;
      const wz = opts.posZ + b.x * sinY + b.z * cosY;
      const wy = this.deckTopY + (b.topAboveDeck ?? spec.bollardHeight) * 0.85;
      this.bollards.push(new DockBollard(b.id, wx, wy, wz));
    }
  }

  /** Look up a bollard anchor by id. */
  getBollard(id: string): DockBollard | undefined {
    return this.bollards.find((b) => b.anchorId === id);
  }

  /** All bollard ids — useful for UI dropdown population. */
  bollardIds(): string[] {
    return this.bollards.map((b) => b.anchorId);
  }
}
